import { useEffect, useRef, useState } from "react";
import type {
  AttendeeMemberSchool,
  AttendeeMemberSchoolList,
  AttendeeSchoolSelection,
} from "@verity-connect/contracts";

/**
 * Where a teacher says which school she works at.
 *
 * It sits under her name and reads as a fact about her, because that is what it
 * is. Her registration always carried a school as text somebody typed, and text
 * connects her to nothing: her school's portal asks for the registrations that
 * name it by id, so until this is answered she is invisible to her own school and
 * her voucher has nowhere to go.
 *
 * Unanswered, it says so in the association's own green and nothing else changes
 * on the page. There is no banner and no badge: the line that always showed her
 * school now asks for it.
 */
export function SchoolChoice({ eventSlug, schoolName, onChosen }: {
  eventSlug: string;
  /** The school text her registration carries, which is not the same as being linked. */
  schoolName: string | null;
  onChosen: (name: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [chosen, setChosen] = useState<string | null>(null);
  const [linked, setLinked] = useState<boolean | null>(null);
  const path = `/api/attendee/events/${encodeURIComponent(eventSlug)}/school`;

  /*
   * Asking the server which school she is attached to, rather than trusting the
   * name beside it.
   *
   * Her registration has always carried a school as text — whatever she or her
   * principal typed — and that text is shown here whether or not it ever matched
   * a school record. A teacher whose typed school matched nothing would therefore
   * have seen her own school's name sitting there looking settled, and would never
   * have been asked the question this control exists to ask. She is exactly who it
   * is for.
   *
   * Until the answer arrives the typed name stays on screen, so the fifty-odd
   * teachers who are properly linked never see their school flicker into a prompt
   * and back.
   */
  useEffect(() => {
    const controller = new AbortController();
    void fetch(path, { credentials: "same-origin", signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error(String(response.status));
        const body = await response.json() as AttendeeMemberSchoolList;
        if (controller.signal.aborted) return;
        setLinked(body.selected !== null);
        const match = body.schools.find((school) => school.id === body.selected);
        if (match) setChosen(match.name);
      })
      .catch(() => { /* Leave the typed name showing; tapping still opens the list. */ });
    return () => controller.abort();
  }, [path]);

  const label = chosen ?? (linked === false ? "Select your school" : schoolName);
  const asking = linked === false && !chosen;

  return (
    <>
      <button
        type="button"
        className={`personal-school-choice${asking ? " is-unset" : ""}`}
        onClick={() => setOpen(true)}
      >
        {label ?? "Select your school"}
        <span aria-hidden="true"> ›</span>
      </button>
      {open && (
        <SchoolSheet
          eventSlug={eventSlug}
          onClose={() => setOpen(false)}
          onChosen={(name) => { setChosen(name); setLinked(true); onChosen(name); setOpen(false); }}
        />
      )}
    </>
  );
}

type Loaded =
  | { status: "loading" }
  | { status: "ready"; schools: readonly AttendeeMemberSchool[]; selected: string | null }
  | { status: "failed"; message: string };

/**
 * The list she chooses from.
 *
 * Town and state ride with every name and are not decoration. Two member schools
 * can share most of a name in two different towns, and choosing the wrong one puts
 * her name, her address and her voucher in a stranger's portal — the one genuinely
 * new way this can leak. The staff control has said the same thing since schools
 * stopped being free text.
 */
function SchoolSheet({ eventSlug, onClose, onChosen }: {
  eventSlug: string;
  onClose: () => void;
  onChosen: (name: string) => void;
}) {
  const [state, setState] = useState<Loaded>({ status: "loading" });
  const [term, setTerm] = useState("");
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const path = `/api/attendee/events/${encodeURIComponent(eventSlug)}/school`;

  useEffect(() => {
    const controller = new AbortController();
    void fetch(path, { credentials: "same-origin", signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error(String(response.status));
        const body = await response.json() as AttendeeMemberSchoolList;
        if (!controller.signal.aborted) {
          setState({ status: "ready", schools: body.schools, selected: body.selected });
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setState({ status: "failed", message: "The list of schools could not be loaded." });
        }
      });
    return () => controller.abort();
  }, [path]);

  useEffect(() => { heading.current?.focus(); }, [state.status]);

  useEffect(() => {
    const escape = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", escape);
    return () => window.removeEventListener("keydown", escape);
  }, [onClose]);

  const choose = async (school: AttendeeMemberSchool) => {
    setSaving(school.id);
    setError(null);
    try {
      const response = await fetch(path, {
        method: "PUT",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ organizationId: school.id }),
      });
      if (!response.ok) throw new Error(String(response.status));
      const result = await response.json() as AttendeeSchoolSelection;
      onChosen(result.organizationName);
    } catch {
      setError("That could not be saved. Try again in a moment.");
      setSaving(null);
    }
  };

  const matches = state.status === "ready"
    ? state.schools.filter((school) => {
        const needle = term.trim().toLocaleLowerCase("en-US");
        if (!needle) return true;
        return `${school.name} ${school.city ?? ""} ${school.state ?? ""}`
          .toLocaleLowerCase("en-US").includes(needle);
      })
    : [];

  return (
    <div className="personal-school-sheet" role="dialog" aria-modal="true" aria-labelledby="school-sheet-title">
      <button type="button" className="personal-school-scrim" aria-label="Close" onClick={onClose} />
      <div className="personal-school-panel">
        <h2 id="school-sheet-title" ref={heading} tabIndex={-1}>Where do you teach?</h2>
        {state.status === "loading" && <p>Loading schools…</p>}
        {state.status === "failed" && <p className="form-error" role="alert">{state.message}</p>}
        {state.status === "ready" && (
          <>
            <label className="personal-school-search">
              <span className="visually-hidden">Search schools</span>
              <input
                type="search"
                value={term}
                placeholder="Search schools"
                autoComplete="off"
                onChange={(event) => setTerm(event.target.value)}
              />
            </label>
            {error && <p className="form-error" role="alert">{error}</p>}
            <ul className="personal-school-list">
              {matches.map((school) => (
                <li key={school.id}>
                  <button
                    type="button"
                    /*
                     * The town is in the name a screen reader reads, not only in the
                     * small print under it. Two member schools can share most of a
                     * name, and somebody who cannot see the second line is exactly
                     * who needs the town said out loud.
                     */
                    aria-label={`${school.name}, ${[school.city, school.state].filter(Boolean).join(", ")}`}
                    aria-current={school.id === state.selected ? "true" : undefined}
                    disabled={saving !== null}
                    onClick={() => void choose(school)}
                  >
                    <span>
                      <strong>{school.name}</strong>
                      <small>{[school.city, school.state].filter(Boolean).join(", ") || "West Virginia"}</small>
                    </span>
                    {school.id === state.selected && <b aria-hidden="true">✓</b>}
                  </button>
                </li>
              ))}
              {matches.length === 0 && (
                <li className="personal-school-empty">
                  No school matches that. Ask at the registration desk and WVCEA can add it.
                </li>
              )}
            </ul>
          </>
        )}
        <button type="button" className="secondary-action" onClick={onClose}>Close</button>
      </div>
    </div>
  );
}
